import { isNumber } from 'lodash'
import { useState } from 'react'
import { useNavigate } from 'react-router'

import { BenchmarkType } from 'enums'
import { PortfolioViewState } from 'enums/PortfolioViewState'
import { Portfolio, PortfolioLinkType } from 'types'

import EditGainsModal from 'components/partials/Portfolio/V2/Modals/EditGainsModal'
import PDSBox from 'designSystem/components/PDSBox/PDSBox'
import PDSButton from 'designSystem/components/PDSButton/PDSButton'
import PDSCard from 'designSystem/components/PDSCard/PDSCard'
import PDSCardHeader from 'designSystem/components/PDSCardHeader/PDSCardHeader'
import PDSFlexBox from 'designSystem/components/PDSFlexBox/PDSFlexBox'
import PDSHeader from 'designSystem/components/PDSHeader/PDSHeader'
import PDSIcon from 'designSystem/components/PDSIcon/PDSIcon'
import PDSSubCard from 'designSystem/components/PDSSubCard/PDSSubCard'
import PDSText from 'designSystem/components/PDSText/PDSText'
import PDSTooltip from 'designSystem/components/PDSTooltip/PDSTooltip'
import { COLOR } from 'designSystem/foundations/constants/color'
import { SIZE } from 'designSystem/foundations/constants/size'
import { formatAsCurrency } from 'utils'
import {
  getPortfolioLinkType,
  getPortfolioViewState,
  shouldLockPortfolio,
} from 'utils/portfolioUtil'

import RealizedGainsTooltipContent from './TooltipContent/RealizedGainsTooltipContent'

type Props = {
  portfolio: Portfolio
}

export default function PortfolioInvestmentPreferencesCard({
  portfolio,
}: Props) {
  const [isEditGainsModalOpen, setIsEditGainsModalOpen] = useState(false)
  const navigate = useNavigate()

  const {
    benchmark,
    investmentPreference,
    investmentPreferencesTemplate,
    isInvestmentPreferenceCompleted,
    realizedGain,
  } = portfolio ?? {}

  const { annualLongTerm, annualShortTerm } = realizedGain ?? {}
  const annualRealizedGainTotal = (annualShortTerm ?? 0) + (annualLongTerm ?? 0)
  const { automation, taxBudget, enableTaxOptimization } =
    investmentPreference ?? {}

  const portfolioViewState = getPortfolioViewState(portfolio)
  const showCardContent = portfolioViewState === PortfolioViewState.NORMAL
  const isLocked = shouldLockPortfolio(portfolio)

  const linkType = getPortfolioLinkType(portfolio)
  const shouldDisplayTaxBudget =
    (linkType === PortfolioLinkType.CharlesSchwab ||
      linkType === PortfolioLinkType.Fidelity) &&
    enableTaxOptimization &&
    taxBudget?.longTermTaxBudget

  const hasRealizedGainsToShow =
    isNumber(annualShortTerm) || isNumber(annualLongTerm)

  let assetClass = 'None'

  if (benchmark) {
    assetClass =
      benchmark.type === BenchmarkType.STOCK
        ? 'Equity Only'
        : 'Multi-Asset Class'
  }

  const navigateInvestmentPreferencesForm = () => {
    navigate(`/investment-preference/${portfolio.id}`)
  }

  const onCloseModal = () => {
    setIsEditGainsModalOpen(false)
  }

  return (
    <>
      <PDSCard>
        <PDSCardHeader>
          <PDSFlexBox justifyContent="space-between">
            <PDSHeader variant="h4">Investment Preferences</PDSHeader>
            {showCardContent && isInvestmentPreferenceCompleted && (
              <PDSButton
                disabled={isLocked}
                onClick={navigateInvestmentPreferencesForm}
                size="sm"
                testId="edit-investment-preferences"
                text="Edit"
                variant="tertiary"
              />
            )}
          </PDSFlexBox>
        </PDSCardHeader>
        {showCardContent &&
          (isInvestmentPreferenceCompleted ? (
            <>
              <PDSBox marginTop={SIZE.s12}>
                <PDSBox display="grid" gap={SIZE.s2}>
                  <PDSText weight="semibold">Automation</PDSText>
                  <PDSText>{automation ? 'On' : 'Off'}</PDSText>
                </PDSBox>
              </PDSBox>

              <PDSBox marginTop={SIZE.s12}>
                <PDSBox display="grid" gap={SIZE.s2}>
                  <PDSText weight="semibold">Asset Class</PDSText>
                  <PDSText>{assetClass}</PDSText>
                </PDSBox>
              </PDSBox>

              <PDSBox marginTop={SIZE.s12}>
                <PDSBox display="grid" gap={SIZE.s2}>
                  <PDSText weight="semibold">Template</PDSText>
                  <PDSText>
                    {investmentPreferencesTemplate
                      ? investmentPreferencesTemplate.displayName
                      : 'None'}
                  </PDSText>
                </PDSBox>
              </PDSBox>

              <PDSBox marginTop={SIZE.s12}>
                <PDSBox display="grid" gap={SIZE.s2}>
                  <PDSText weight="semibold">Selected Benchmark</PDSText>
                  <PDSText>{benchmark ? benchmark.name : 'None'}</PDSText>
                </PDSBox>
              </PDSBox>
              <PDSBox marginTop={SIZE.s12}>
                {(shouldDisplayTaxBudget || hasRealizedGainsToShow) && (
                  <PDSBox display="grid" gap={SIZE.s2}>
                    <PDSFlexBox alignItems="center" gap={SIZE.s8}>
                      <PDSText size="sm" weight="semibold">
                        {shouldDisplayTaxBudget
                          ? 'Gains Budget Utilization'
                          : 'Total Realized Gains'}
                      </PDSText>
                      <PDSTooltip content={<RealizedGainsTooltipContent />} />
                    </PDSFlexBox>
                    <PDSFlexBox
                      justifyContent="space-between"
                      alignItems="center"
                      alignSelf="stretch"
                    >
                      <PDSText>
                        <PDSText weight="semibold">
                          {formatAsCurrency(annualRealizedGainTotal)}
                        </PDSText>{' '}
                        {shouldDisplayTaxBudget && (
                          <PDSText color={COLOR.textButtonPrimary_disabled}>
                            / {formatAsCurrency(taxBudget?.longTermTaxBudget)}
                          </PDSText>
                        )}
                      </PDSText>
                      {shouldDisplayTaxBudget && (
                        <PDSButton
                          text="Edit Budget"
                          iconLeft="pencil"
                          variant="tertiary"
                          size="sm"
                          onClick={() => setIsEditGainsModalOpen(true)}
                        />
                      )}
                    </PDSFlexBox>
                  </PDSBox>
                )}
              </PDSBox>
            </>
          ) : (
            <PDSBox marginTop={SIZE.s20}>
              <PDSSubCard
                alignItems="center"
                backgroundColor={COLOR.action100}
                display="flex"
                flexDirection="column"
                gap={SIZE.s16}
                paddingSize="lg"
                showBorder={false}
              >
                <PDSIcon
                  color={COLOR.textSecondary}
                  name="adjustments"
                  size="md"
                />
                <PDSText textAlign="center" weight="semibold">
                  Investment Preferences Setup
                </PDSText>
                <PDSText textAlign="center">
                  Configure your investment preferences to get access to Pave
                  Pro’s trade suggestions.
                </PDSText>
              </PDSSubCard>
              <PDSButton
                marginTop={SIZE.s20}
                onClick={navigateInvestmentPreferencesForm}
                size="md-f"
                testId="edit-investment-preferences"
                text="Set Investment Preferences"
              />
            </PDSBox>
          ))}
      </PDSCard>
      {shouldDisplayTaxBudget && (
        <EditGainsModal
          isOpen={isEditGainsModalOpen}
          onClose={onCloseModal}
          annualRealizedGainTotal={annualRealizedGainTotal}
          taxBudget={taxBudget}
          portfolio={portfolio}
        />
      )}
    </>
  )
}
